import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import SectionHeading from "@/components/SectionHeading";
import ZeffyLink from "@/components/ZeffyLink";
import AnniversaryCountdown from "@/components/AnniversaryCountdown";
import { IMAGES, HOST_HOTEL, HOTEL_2026_PRESIDENTS, AFFILIATIONS } from "@/lib/constants";
import {
  ZEFFY_INTEREST_LIST,
  ZEFFY_UNIFIED,
  ZEFFY_VOLUNTEER,
} from "@/lib/api";
import {
  ArrowUpRight,
  Calendar,
  MapPin,
  Hotel,
  Phone,
  Sparkles,
  Music,
  Users,
  Megaphone,
  ClipboardList,
  Compass,
  Flame,
  CalendarClock,
  BookOpen,
  HandHeart,
} from "lucide-react";

const AGENDA = [
  {
    d: "Wednesday, May 12, 2027",
    t: "Arrivals · NCR Presidents Meeting · Explore Detroit",
    b: "Early registration opens. The NCR Presidents Meeting convenes. Open afternoon to settle in and explore Detroit.",
  },
  {
    d: "Thursday, May 13, 2027",
    t: "Opening Ceremonies · Dignitary Remarks · NCR Official Business",
    b: "Registration continues. Opening ceremonies and dignitary remarks set the tone for the week. Lunch follows. NCR official business sessions in the afternoon, with the hospitality suite open and time to explore Detroit in the evening.",
  },
  {
    d: "Friday, May 14, 2027",
    t: "Breakout & Personal Development · Cookout at Historic Engine 11",
    b: "Registration continues. Breakout and personal development sessions in the morning, lunch, and additional breakout sessions in the afternoon. The evening moves to a Phoenix cookout at Historic Engine 11, followed by the hospitality suite and time to explore Detroit.",
  },
  {
    d: "Saturday, May 15, 2027",
    t: "Community Outreach (AM) · Phoenix 60th Anniversary Gala (7:00 p.m.)",
    b: "A morning community outreach event in Detroit. Afternoon is open for free time and exploring Detroit. The evening is anchored by the signature celebration — the Phoenix 60th Anniversary Gala: The Detroit Renaissance Ball at 7:00 p.m., a black-tie evening honoring 60 years of Phoenix legacy, advocacy, brotherhood, sisterhood, and community impact.",
    featured: true,
  },
  {
    d: "Sunday, May 16, 2027",
    t: "Departures · Optional Brunch at Fishbones",
    b: "Hotel checkout and departures. An optional brunch at Fishbones for attendees with later flights.",
  },
];

const SPONSOR_REASONS = [
  { icon: Sparkles, t: "Visibility", b: "Showcase your organization to firefighters, retirees, and IABPFF leadership from across the North Central Region." },
  { icon: Users, t: "Community Impact", b: "Sponsorship dollars sustain Phoenix programs — Project 100, outreach, Engine 11 preservation, and active and retired firefighter fellowship." },
  { icon: Megaphone, t: "Legacy Connection", b: "Stand alongside an organization with six decades of service to Detroit firefighters and the communities of Detroit." },
];

export default function Conference2027Anniversary() {
  return (
    <div data-testid="conference-anniversary-page">
      <PageMeta
        title="2027 NCR Spring Conference & Phoenix 60th Anniversary"
        description="May 12–16, 2027 · Detroit, Michigan. The Phoenix of the Detroit Fire Department hosts the 2027 North Central Region Spring Conference, anchored by the Phoenix 60th Anniversary Gala: The Detroit Renaissance Ball on Saturday, May 15, 2027."
        path="/2027"
      />
      <PageHero
        overline="May 12–16, 2027 · Detroit, Michigan"
        title="The 2027 NCR Spring Conference & Phoenix 60th Anniversary."
        subtitle="The Phoenix of the Detroit Fire Department will host the 2027 North Central Region Spring Conference in Detroit. The signature anniversary celebration of our 60-year milestone — the Phoenix 60th Anniversary Gala: The Detroit Renaissance Ball — takes place Saturday, May 15, 2027 at 7:00 p.m. during the conference."
        image={IMAGES.anniversaryBall}
      >
        <div className="flex flex-col sm:flex-row sm:items-start gap-5">
          <div className="flex flex-col gap-4 flex-1 min-w-0">
            {/* PRIMARY — Unified conference action (Register, Sponsor, Advertise, or Vendor) */}
            <ZeffyLink href={ZEFFY_UNIFIED} className="btn-gold w-full sm:w-auto justify-center text-base font-semibold shadow-lg shadow-amber-500/20" testid="2027-hero-unified">
              Register, Sponsor, Advertise, or Vendor <ArrowUpRight className="h-5 w-5" />
            </ZeffyLink>

            {/* What's inside the combined campaign — small explainer line */}
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed max-w-2xl">
              One Zeffy campaign covers full conference registration, Thursday
              luncheon-only tickets, gala-only tickets, VIP Sponsorship Packages,
              Gala Table Sponsorships, Event/Program Support, Program Book ads,
              and Vendor/Exhibitor opportunities.
            </p>

            {/* SECONDARY — interest list + hotel, smaller and lower */}
            <div className="flex flex-col sm:flex-row gap-2 mt-1 text-sm">
              <ZeffyLink href={ZEFFY_INTEREST_LIST} className="btn-ghost w-full sm:w-auto justify-center" testid="2027-hero-interest">
                Join the Interest List <ArrowUpRight className="h-4 w-4" />
              </ZeffyLink>
              <a href="#host-hotel" className="btn-ghost w-full sm:w-auto justify-center" data-testid="2027-hero-hotel">
                View Host Hotel
              </a>
            </div>
          </div>

          {/* QR CODE — Unified Zeffy campaign */}
          <a
            href={ZEFFY_UNIFIED}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 self-center sm:self-start rounded-xl bg-white p-3 shadow-lg shadow-black/30 border border-amber-500/30 hover:border-amber-500/70 transition-colors group"
            data-testid="2027-hero-qr"
            aria-label="Scan to Register, Sponsor, Advertise, or Vendor"
          >
            <img
              src="/photos/zeffy-2027-qr.png"
              alt="QR code — Scan to Register, Sponsor, Advertise, or Vendor on Zeffy"
              className="block w-[140px] h-[140px] sm:w-[150px] sm:h-[150px] object-contain"
              loading="lazy"
            />
            <p className="mt-2 text-center text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-slate-800 leading-tight max-w-[150px]">
              Scan to Register, Sponsor,<br />Advertise, or Vendor
            </p>
          </a>
        </div>
      </PageHero>

      {/* ----- COUNTDOWN ----- */}
      <section className="px-5 lg:px-8 -mt-6 lg:-mt-10" data-testid="2027-countdown">
        <div className="max-w-7xl mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-5 sm:p-6 lg:p-8">
          <div className="overline mb-3 flex items-center gap-2">
            <Flame className="h-4 w-4 text-amber-500 shrink-0" />
            <span>Countdown to the Detroit Renaissance Ball</span>
          </div>
          <AnniversaryCountdown />
          <div className="text-slate-400 text-xs mt-4 leading-relaxed">
            <div>Saturday, May 15, 2027 · 7:00 p.m.</div>
            <div className="mt-0.5">{HOST_HOTEL.name}, Detroit, MI</div>
          </div>
        </div>
      </section>

      {/* ----- AFFILIATION BAND (IABPFF + NCR) — restrained, protocol-appropriate ----- */}
      <section className="px-5 lg:px-8 mt-8 lg:mt-12" data-testid="affiliations">
        <div className="max-w-7xl mx-auto rounded-2xl border border-white/10 bg-white/[0.02] px-6 sm:px-8 py-6 sm:py-8">
          <div className="text-center">
            <div className="overline">Hosted by the Phoenix of the Detroit Fire Department</div>
            <p className="text-slate-300 text-sm sm:text-base mt-2 leading-relaxed max-w-2xl mx-auto">
              For the North Central Region of the International Association of Black Professional Fire Fighters.
            </p>
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10 items-center max-w-3xl mx-auto">
            <div className="flex flex-col items-center text-center" data-testid="affiliation-iabpff">
              <img
                src={AFFILIATIONS.iabpff.imageUrl}
                alt={AFFILIATIONS.iabpff.alt}
                className="h-20 w-20 sm:h-24 sm:w-24 object-contain"
                loading="lazy"
              />
              <div className="mt-3 font-display text-base text-white">{AFFILIATIONS.iabpff.name}</div>
              <div className="text-xs text-slate-500 tracking-wider uppercase mt-1">{AFFILIATIONS.iabpff.motto}</div>
            </div>
            <div className="flex flex-col items-center text-center" data-testid="affiliation-ncr">
              <img
                src={AFFILIATIONS.ncr.imageUrl}
                alt={AFFILIATIONS.ncr.alt}
                className="h-20 w-20 sm:h-24 sm:w-24 object-contain"
                loading="lazy"
              />
              <div className="mt-3 font-display text-base text-white">{AFFILIATIONS.ncr.name}</div>
              <div className="text-xs text-slate-500 tracking-wider uppercase mt-1">{AFFILIATIONS.ncr.motto}</div>
            </div>
          </div>

          {/* IABPFF / NCR Institutional Legacy Declaration — folded into the protocol band */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center" data-testid="iabpff-motto">
            <div className="overline text-amber-500 text-[10px]">IABPFF · North Central Region</div>
            <blockquote className="font-display text-xl lg:text-2xl text-white mt-4 leading-relaxed italic">
              "All that we are, we owe.<br />We live eternally in the RED."
            </blockquote>
          </div>
        </div>
      </section>

      {/* ----- TWO OVERVIEWS ----- */}
      <section className="py-12 lg:py-28" data-testid="2027-overviews">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-dark p-8 lg:p-10" data-testid="overview-conference">
            <div className="overline flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-amber-500" /> The Conference
            </div>
            <h3 className="font-display text-3xl lg:text-4xl text-white mt-3 leading-tight">
              2027 NCR Spring Conference.
            </h3>
            <div className="divider-gold" />
            <p className="text-slate-300 leading-relaxed">
              Five days of fellowship, leadership development, and service —
              May 12–16, 2027 — hosted in Detroit by the Phoenix of the Detroit
              Fire Department for the North Central Region of the IABPFF.
              Workshops, business sessions, a Community Day, and cultural
              programming bring together firefighters and chapters from across
              the region.
            </p>
          </div>
          <div className="card-dark p-8 lg:p-10" data-testid="overview-anniversary">
            <div className="overline flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" /> The 60th Anniversary
            </div>
            <h3 className="font-display text-3xl lg:text-4xl text-white mt-3 leading-tight">
              Sixty years of Phoenix.
            </h3>
            <div className="divider-gold" />
            <p className="text-slate-300 leading-relaxed">
              In 2027, the Phoenix of the Detroit Fire Department marks sixty
              years of advocacy, mentorship, and service to Detroit firefighters
              and the communities of Detroit — a milestone forged across six
              decades of leadership, brotherhood, sisterhood, and Phoenix legacy.
            </p>
          </div>
        </div>
      </section>

      {/* ----- SUPPORT OPPORTUNITIES (moved up — appears directly after the two overview cards, before the Gala) ----- */}
      <section id="support" className="py-12 lg:py-28 scroll-mt-24" data-testid="support-opportunities-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {/* Standalone sponsorship intro band — leads directly into the four active support paths below */}
          <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-6 lg:p-8 mb-12" data-testid="sponsorship-intro">
            <div className="overline">2027 Event-Specific Support</div>
            <p className="text-slate-200 mt-2 text-sm lg:text-base leading-relaxed max-w-4xl">
              VIP sponsorship, Program Book ads, Gala table sponsorships, and program support opportunities for the 2027 North Central Region Spring Conference and the Phoenix 60th Anniversary Gala live here on the 2027 page. Choose a path below to view packages and complete your selection on Zeffy.
            </p>
          </div>

          <SectionHeading
            overline="Support the 2027 Conference & 60th Anniversary"
            title="Stand with the Phoenix."
            subtitle="Four active support paths are now open for the 2027 NCR Spring Conference and the Phoenix 60th Anniversary Gala. Choose the option that best fits your organization and complete your selection on Zeffy."
          />

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* VIP Sponsorship Packages */}
            <div className="card-dark p-7 flex flex-col" data-testid="support-path-sponsorship">
              <Megaphone className="h-7 w-7 text-amber-500" />
              <div className="overline mt-5">VIP Sponsorship</div>
              <h3 className="font-display text-2xl text-white mt-2 leading-tight">VIP Sponsorship Packages.</h3>
              <p className="text-slate-400 mt-4 text-sm leading-relaxed flex-1">
                Multi-tier VIP sponsorship across the 2027 NCR Spring Conference and the Phoenix 60th Anniversary Gala. View available packages, recognition tiers, and select the option that best fits your organization.
              </p>
              <ZeffyLink href={ZEFFY_UNIFIED} className="btn-gold w-full justify-center mt-6" testid="support-cta-sponsorship">
                View VIP Sponsorship Packages <ArrowUpRight className="h-4 w-4" />
              </ZeffyLink>
              <p className="text-slate-500 text-xs mt-4 leading-relaxed">
                Custom support opportunities may be discussed through the VIP sponsorship package form.
              </p>
            </div>

            {/* Program Book Ad Placements */}
            <div className="card-dark p-7 flex flex-col" data-testid="support-path-program-book">
              <BookOpen className="h-7 w-7 text-amber-500" />
              <div className="overline mt-5">Program Book</div>
              <h3 className="font-display text-2xl text-white mt-2 leading-tight">Program Book Ad Placements.</h3>
              <p className="text-slate-400 mt-4 text-sm leading-relaxed flex-1">
                Purchase an ad, tribute, business message, or recognition placement in the 2027 North Central Region Conference and 60th Anniversary Commemorative Program Book.
              </p>
              <ZeffyLink href={ZEFFY_UNIFIED} className="btn-gold w-full justify-center mt-6" testid="support-cta-program-book">
                Purchase Program Book Ad <ArrowUpRight className="h-4 w-4" />
              </ZeffyLink>
            </div>

            {/* Gala Table Sponsorships */}
            <div className="card-dark p-7 flex flex-col" data-testid="support-path-gala-tables">
              <Users className="h-7 w-7 text-amber-500" />
              <div className="overline mt-5">Gala Tables</div>
              <h3 className="font-display text-2xl text-white mt-2 leading-tight">Gala Table Sponsorships.</h3>
              <p className="text-slate-400 mt-4 text-sm leading-relaxed flex-1">
                Sponsor a table at the Phoenix 60th Anniversary Gala on Saturday, May 15, 2027. View available table sponsorship options and reserve your seating on Zeffy.
              </p>
              <ZeffyLink href={ZEFFY_UNIFIED} className="btn-gold w-full justify-center mt-6" testid="support-cta-gala-tables">
                Sponsor a Gala Table <ArrowUpRight className="h-4 w-4" />
              </ZeffyLink>
            </div>

            {/* Program Support Sponsorships */}
            <div className="card-dark p-7 flex flex-col" data-testid="support-path-program-support">
              <HandHeart className="h-7 w-7 text-amber-500" />
              <div className="overline mt-5">Program Support</div>
              <h3 className="font-display text-2xl text-white mt-2 leading-tight">Program Support Sponsorships.</h3>
              <p className="text-slate-400 mt-4 text-sm leading-relaxed flex-1">
                Underwrite a specific conference or anniversary program element — hospitality, Community Day, registration, signage, awards, or another milestone initiative. Choose an option that aligns with a meaningful piece of the milestone.
              </p>
              <ZeffyLink href={ZEFFY_UNIFIED} className="btn-gold w-full justify-center mt-6" testid="support-cta-program-support">
                View Program Support Sponsorships <ArrowUpRight className="h-4 w-4" />
              </ZeffyLink>
            </div>
          </div>

          {/* Why your support matters — existing 3 evergreen cards kept as supporting context */}
          <div className="mt-16">
            <div className="overline text-center">Why your support matters</div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {SPONSOR_REASONS.map((r) => (
                <div key={r.t} className="card-dark p-7" data-testid={`sponsor-reason-${r.t.toLowerCase()}`}>
                  <r.icon className="h-7 w-7 text-amber-500" />
                  <div className="font-display text-xl text-white mt-5">{r.t}</div>
                  <div className="text-slate-400 mt-3 text-sm leading-relaxed">{r.b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ----- HARLEM RENAISSANCE BALL ----- */}
      <section className="py-12 lg:py-28 bg-[#0E0E0E]" data-testid="ball-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden ring-ember p-8 lg:p-14">
            <img
              src={IMAGES.anniversaryBall}
              alt="2027 NCR Spring Conference Save the Date — Phoenix 60th Anniversary · May 12-16, 2027"
              className="absolute inset-0 w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-red-950/50" />
            <div className="relative">
              <div className="overline">Signature Anniversary Celebration · Saturday, May 15, 2027 · 7:00 p.m.</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-3 leading-[1.05]">
                Phoenix 60th Anniversary Gala:
                <span className="block text-amber-500">The Detroit Renaissance Ball.</span>
              </h2>
              <div className="divider-gold" />
              <p className="text-slate-200 leading-relaxed max-w-2xl">
                A black-tie evening of elegance, culture, and tribute — held
                during the 2027 NCR Spring Conference. An homage to Black
                artistry, Detroit soul, and the Phoenix rising.
              </p>
              <dl className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div className="card-dark p-4">
                  <dt className="overline">Date</dt>
                  <dd className="text-white mt-1 flex items-center gap-2"><Calendar className="h-4 w-4 text-amber-500" /> Sat, May 15, 2027 · 7:00 p.m.</dd>
                </div>
                <div className="card-dark p-4">
                  <dt className="overline">Host Hotel</dt>
                  <dd className="text-white mt-1 flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-500" /> {HOST_HOTEL.name}</dd>
                  <dd className="text-slate-400 text-xs mt-1">{HOST_HOTEL.address}</dd>
                </div>
                <div className="card-dark p-4">
                  <dt className="overline">Attire</dt>
                  <dd className="text-white mt-1 flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /> Black-Tie</dd>
                </div>
              </dl>
              <p className="text-slate-500 text-xs mt-8 max-w-3xl">
                Final gala ticketing and table pricing will be announced as
                planning continues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ----- HOST HOTEL + ROOM BLOCK ----- */}
      <section id="host-hotel" className="py-12 lg:py-28 scroll-mt-24" data-testid="host-hotel-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Host Hotel · Room Block"
            title="Stay at the host hotel."
            subtitle={`Hollywood Casino Greektown is the official host hotel for the full 2027 NCR Spring Conference and the Phoenix 60th Anniversary Gala. A Phoenix room block is now open.`}
          />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="card-dark p-8 lg:col-span-7">
              <div className="overline flex items-center gap-2"><Hotel className="h-4 w-4 text-amber-500" /> Host Hotel</div>
              <h3 className="font-display text-3xl lg:text-4xl text-white mt-3 leading-tight">{HOST_HOTEL.name}</h3>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-amber-500 mt-0.5" />
                  <div>
                    <div className="text-slate-200">{HOST_HOTEL.address}</div>
                    <div className="text-slate-500 text-xs">{HOST_HOTEL.city}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="h-4 w-4 text-amber-500 mt-0.5" />
                  <div>
                    <div className="text-slate-200">
                      <a href={`tel:${HOST_HOTEL.reservationPhone.replace(/\D/g, "")}`} className="hover:text-amber-400">{HOST_HOTEL.reservationPhone}</a>
                    </div>
                    <div className="text-slate-500 text-xs">Reservations phone</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ClipboardList className="h-4 w-4 text-amber-500 mt-0.5" />
                  <div>
                    <div className="text-slate-200">Group Code: <span className="text-amber-400 font-accent tracking-wider">{HOST_HOTEL.groupCode}</span></div>
                    <div className="text-slate-500 text-xs">Booking cut-off: {HOST_HOTEL.cutoffDate}</div>
                  </div>
                </div>
              </div>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a
                  href={HOST_HOTEL.bookingLink}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold w-full sm:w-auto justify-center"
                  data-testid="hotel-book-now"
                >
                  Book Hotel Room Block <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href={`https://www.google.com/maps?q=${encodeURIComponent(HOST_HOTEL.mapsQuery)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost w-full sm:w-auto justify-center"
                  data-testid="hotel-view-map"
                >
                  View on Map
                </a>
              </div>
            </div>
            <div className="card-dark p-8 lg:col-span-5">
              <div className="overline flex items-center gap-2"><Compass className="h-4 w-4 text-amber-500" /> Travel Tips</div>
              <ul className="mt-5 space-y-3 text-sm text-slate-300 leading-relaxed">
                <li>• Book within the Phoenix room block to support the conference and lock in the negotiated rate.</li>
                <li>• Cut-off date for the group rate is <span className="text-amber-400">{HOST_HOTEL.cutoffDate}</span> — book early.</li>
                <li>• Detroit Metropolitan Wayne County Airport (DTW) is approximately 25 minutes from the host hotel.</li>
                <li>• Both the conference programming and the Detroit Renaissance Ball take place at the host hotel.</li>
              </ul>
            </div>
          </div>

          {/* In the Neighborhood — folded into the Host Hotel section as a smaller supporting callout */}
          <div className="mt-6 rounded-2xl border border-slate-800 bg-black/40 px-6 py-5 lg:px-8 lg:py-6" data-testid="host-hotel-neighborhood">
            <div className="flex flex-col lg:flex-row lg:items-start gap-3 lg:gap-8">
              <div className="lg:w-48 flex-shrink-0">
                <div className="overline flex items-center gap-2 text-amber-500">
                  <MapPin className="h-4 w-4" /> In the Neighborhood
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-slate-400 text-sm leading-relaxed">
                  Hollywood Casino Greektown sits inside Detroit's historic
                  Greektown district — one of the city's most walkable
                  neighborhoods. The host hotel is approximately 2.0 miles
                  from Historic Engine 11. Greek tavernas, Mediterranean
                  pastries, outdoor cafés, and downtown landmarks like the
                  Spirit of Detroit and Campus Martius are just steps from
                  the hotel door. Come a day early. Stay a day late.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- IN THE NEIGHBORHOOD — folded into Host Hotel section above ----- */}

      {/* ----- INSTITUTIONAL LEGACY DECLARATION — moved into the protocol band near the top ----- */}

      {/* ----- 2026 NCR PRESIDENT'S BOARD MEETING — moved to page bottom as footnote-style band ----- */}

      {/* ----- AGENDA / SCHEDULE ----- */}
      <section className="py-12 lg:py-28 bg-[#0E0E0E]" data-testid="agenda-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="At-a-Glance Schedule"
            title="The week in Detroit."
            subtitle="Final session times, speakers, and venue rooms will be confirmed in the official conference program. The Phoenix 60th Anniversary Gala anchors Saturday evening."
          />
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6">
            {AGENDA.map((a) => (
              <div
                key={a.t}
                className={`card-dark p-4 sm:p-6 lg:p-7 ${a.featured ? "ring-1 ring-amber-500/40 col-span-2 lg:col-span-1" : ""}`}
                data-testid={`agenda-${a.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="overline text-[10px] sm:text-xs">{a.d}</div>
                <div className={`font-display mt-3 text-white leading-tight ${a.featured ? "text-lg sm:text-xl lg:text-2xl" : "text-base sm:text-lg lg:text-xl"}`}>{a.t}</div>
                <p className="text-slate-400 mt-3 text-xs sm:text-sm leading-relaxed">{a.b}</p>
                {a.featured && (
                  <div className="mt-4 inline-block text-[10px] tracking-[0.28em] uppercase font-accent text-black bg-amber-500 px-2 py-1 rounded">
                    Signature Anniversary Celebration
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-xs leading-relaxed mt-10 max-w-3xl">
            Schedule details are subject to change as final planning is
            confirmed.
          </p>
        </div>
      </section>

      {/* ----- SPONSORSHIP — moved up and expanded into Support Opportunities above ----- */}

      {/* ----- VOLUNTEER ----- */}
      <section className="py-12 lg:py-28 bg-[#0E0E0E]" data-testid="volunteer-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden ring-ember p-8 lg:p-12">
            <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-red-950/40" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="overline flex items-center gap-2"><Users className="h-4 w-4 text-amber-500" /> Volunteer Interest</div>
                <h3 className="font-display text-3xl lg:text-5xl text-white mt-3 leading-[1.05]">
                  Be part of the
                  <span className="block text-amber-500">Phoenix 2027 team.</span>
                </h3>
                <p className="text-slate-300 mt-5 leading-relaxed max-w-2xl">
                  Volunteer opportunities span registration, hospitality,
                  transportation, setup and logistics, Community Day, and ball
                  hospitality. Submit a volunteer interest form to receive
                  details as roles are confirmed.
                </p>
              </div>
              <div className="lg:col-span-4 flex">
                <ZeffyLink href={ZEFFY_VOLUNTEER} className="btn-gold w-full justify-center" testid="volunteer-cta">
                  Volunteer Interest <ArrowUpRight className="h-4 w-4" />
                </ZeffyLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- CLOSING · UNIFIED CONFERENCE ACTION ----- */}
      <section className="py-12 lg:py-28" data-testid="registration-section">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="overline flex items-center justify-center gap-2"><Music className="h-4 w-4 text-amber-500" /> Register · Sponsor · Advertise · Vendor</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-3 leading-[1.05]">
            The 2027 conference is
            <span className="block text-amber-500">live and open.</span>
          </h2>
          <div className="divider-gold mx-auto" />
          <p className="text-slate-400 mt-6 leading-relaxed max-w-2xl mx-auto">
            One combined Zeffy campaign now covers full conference
            registration, Thursday luncheon-only tickets, gala-only tickets,
            VIP Sponsorship Packages, Gala Table Sponsorships, Event/Program
            Support, Program Book ads, and Vendor/Exhibitor opportunities.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6">
            <ZeffyLink href={ZEFFY_UNIFIED} className="btn-gold w-full sm:w-auto justify-center text-base font-semibold inline-flex shadow-lg shadow-amber-500/20" testid="closing-unified">
              Register, Sponsor, Advertise, or Vendor <ArrowUpRight className="h-5 w-5" />
            </ZeffyLink>

            {/* QR CODE — Unified Zeffy campaign (closing) */}
            <a
              href={ZEFFY_UNIFIED}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 rounded-xl bg-white p-3 shadow-lg shadow-black/30 border border-amber-500/30 hover:border-amber-500/70 transition-colors"
              data-testid="closing-qr"
              aria-label="Scan to Register, Sponsor, Advertise, or Vendor"
            >
              <img
                src="/photos/zeffy-2027-qr.png"
                alt="QR code — Scan to Register, Sponsor, Advertise, or Vendor on Zeffy"
                className="block w-[140px] h-[140px] sm:w-[150px] sm:h-[150px] object-contain"
                loading="lazy"
              />
              <p className="mt-2 text-center text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-slate-800 leading-tight max-w-[150px]">
                Scan to Register, Sponsor,<br />Advertise, or Vendor
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* ----- 2026 NCR PRESIDENT'S BOARD MEETING (Footnote band — separate event, distinct from 2027) ----- */}
      <section
        id="ncr-presidents-2026"
        className="pb-16 lg:pb-20 scroll-mt-24"
        data-testid="ncr-presidents-2026-section"
      >
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="rounded-xl border border-slate-800/60 bg-black/30 px-5 py-5 lg:px-6 lg:py-6">
            <div className="flex flex-col lg:flex-row lg:items-start gap-5">
              <div className="flex-1 min-w-0">
                <div className="overline flex items-center gap-2 text-slate-500 text-[10px]">
                  <CalendarClock className="h-3.5 w-3.5 text-slate-500" /> Footnote · Separate Event · 2026
                </div>
                <h3 className="font-display text-base lg:text-lg text-slate-200 mt-2 leading-tight">
                  2026 North Central Region Presidents Meeting
                </h3>
                <p className="text-slate-400 leading-relaxed mt-2 text-xs sm:text-sm">
                  Phoenix is also hosting the NCR Presidents Meeting at{" "}
                  {HOTEL_2026_PRESIDENTS.hotel} from {HOTEL_2026_PRESIDENTS.dates}.
                  A Phoenix room block is open at the host hotel — book within
                  the block to lock in the group rate. This is distinct from
                  the 2027 NCR Spring Conference and Phoenix 60th Anniversary
                  detailed above.
                </p>

                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <a
                    href={HOTEL_2026_PRESIDENTS.bookingLink}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost w-full sm:w-auto justify-center text-xs"
                    data-testid="ncr-presidents-2026-book"
                  >
                    Book 2026 Room Block <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                  <a
                    href={`tel:${HOTEL_2026_PRESIDENTS.reservationPhone.replace(/\D/g, "")}`}
                    className="btn-ghost w-full sm:w-auto justify-center text-xs"
                    data-testid="ncr-presidents-2026-call"
                  >
                    <Phone className="h-3.5 w-3.5" /> Call {HOTEL_2026_PRESIDENTS.reservationPhone}
                  </a>
                </div>
              </div>

              <div className="lg:w-56 lg:flex-none lg:border-l lg:border-slate-800 lg:pl-5">
                <dl className="text-[11px] space-y-2 text-slate-500">
                  <div>
                    <dt className="overline text-slate-600 text-[9px]">Dates</dt>
                    <dd className="text-slate-300 mt-0.5">{HOTEL_2026_PRESIDENTS.dates}</dd>
                  </div>
                  <div>
                    <dt className="overline text-slate-600 text-[9px]">Group Code</dt>
                    <dd className="text-amber-400/80 mt-0.5 font-accent tracking-wider" data-testid="ncr-presidents-2026-group-code">
                      {HOTEL_2026_PRESIDENTS.groupCode}
                    </dd>
                    <dd className="text-slate-600 mt-0.5">
                      Cut-off: {HOTEL_2026_PRESIDENTS.cutoffDate}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
